import { ref, onUnmounted, watch, unref } from "vue";
import moment from "moment";
import { useIntervalManager } from "./useIntervalManager";

export function useLiveAge(timestamp, locale) {
  const age = ref("");
  const { addInterval, removeInterval } = useIntervalManager();
  let intervalId = null;

  const calculateAge = (timestamp) => {
    const actualTimestamp = unref(timestamp);

    if (!actualTimestamp) {
      return "";
    }

    // Explicitly parse as UTC
    const start = moment.utc(actualTimestamp);
    // Use moment.utc() for current time as well for consistent comparison
    const now = moment.utc();

    // Check if the date is valid after parsing
    if (!start.isValid()) {
      console.error("Moment could not parse timestamp:", actualTimestamp);
      return "Invalid Date";
    }

    const totalMinutes = now.diff(start, "minutes");
    const totalHours = now.diff(start, "hours");
    const totalDays = now.diff(start, "days");
    const totalYears = now.diff(start, "years");

    // До 10 минут - показываются минуты с секундами в лайве
    if (totalMinutes < 10) {
      const seconds = now.diff(start, "seconds") % 60;
      return locale?.value !== "en"
        ? `${totalMinutes}м ${seconds}с`
        : `${totalMinutes}m ${seconds}s`;
    }

    // До 60 минут, показываются только минуты
    if (totalMinutes < 60) {
      return locale?.value !== "en" ? `${totalMinutes}м` : `${totalMinutes}m`;
    }

    // Более 60 минут — минуты, часы
    if (totalHours < 24) {
      const hours = Math.floor(totalHours);
      const minutes = totalMinutes % 60;
      return locale?.value !== "en"
        ? `${hours}ч ${minutes}м`
        : `${hours}h ${minutes}m`;
    }

    // Более 1 дня — только дни
    if (totalDays < 365) {
      return locale?.value !== "en" ? `${totalDays}д` : `${totalDays}d`;
    }

    // Более 365 дней — только года и дни
    const years = Math.floor(totalYears);
    const remainingDays = totalDays % 365;
    return locale?.value !== "en"
      ? `${years}г ${remainingDays}д`
      : `${years}y ${remainingDays}d`;
  };

  const startLiveUpdate = () => {
    const actualTimestamp = unref(timestamp);

    if (!actualTimestamp) {
      age.value = "";
      return;
    }

    // Сначала вычисляем возраст
    age.value = calculateAge(timestamp);

    // Проверяем, нужно ли живое обновление (только для первых 10 минут)
    const start = moment.utc(actualTimestamp);
    const now = moment.utc();
    const totalMinutes = now.diff(start, "minutes");

    if (totalMinutes < 10) {
      // Обновляем каждую секунду для первых 10 минут
      intervalId = setInterval(() => {
        age.value = calculateAge(timestamp);

        // Останавливаем интервал, если прошло больше 10 минут
        const currentMinutes = moment.utc().diff(start, "minutes");
        if (currentMinutes >= 10) {
          stopLiveUpdate();
        }
      }, 1000);

      // Добавляем интервал в глобальный менеджер
      addInterval(intervalId);
    }
  };

  const stopLiveUpdate = () => {
    if (intervalId) {
      removeInterval(intervalId);
      intervalId = null;
    }
  };

  // Следим за изменениями timestamp
  watch(
    timestamp,
    () => {
      stopLiveUpdate();
      startLiveUpdate();
    },
    { immediate: true },
  );

  // Пересчитываем возраст при смене языка
  if (locale) {
    watch(locale, () => {
      age.value = calculateAge(timestamp);
    });
  }

  // Автоматически очищаем интервал при размонтировании
  onUnmounted(() => {
    stopLiveUpdate();
  });

  return {
    age,
    startLiveUpdate,
    stopLiveUpdate,
  };
}
